/**
 * One place for clock times (#time-format): the 12- or 24-hour preference (Settings → My account → Preferences,
 * default 24-hour), compact chips ("6–14" / "6 AM–2 PM", "6AM–2PM" where space is tight), and overnight shifts, which show the day they end
 * ("22:00–06:00 Thu") instead of "(+1)". CSV exports stay 24-hour ISO and don't use this.
 */
import { DateTime } from 'luxon';

export type ClockPref = '24h' | '12h';
export const CLOCK_PREFS: { id: ClockPref; label: string; example: string }[] = [
  { id: '24h', label: '24-hour', example: '14:30' }, { id: '12h', label: '12-hour', example: '2:30 PM' },
];
export const isClockPref = (v: unknown): v is ClockPref => v === '24h' || v === '12h';

const asTime = (t: DateTime | string): { h: number; m: number } => {
  if (typeof t !== 'string') return { h: t.hour, m: t.minute };
  const [h, m] = t.split(':').map(Number);
  return { h: h ?? 0, m: m ?? 0 };
};
const pad = (n: number) => String(n).padStart(2, '0');

/**
 * How short: false = full ("06:00", "2:30 PM"); true = compact, no ":00" ("6", "6:30"; "6 AM", "12 PM");
 * 'tight' = compact without the space for the narrowest spots ("6AM", "6:30PM"; 24-hour is the same as compact).
 */
export type ClockCompact = boolean | 'tight';

/**
 * A time of day ("HH:mm" or a DateTime already in the right zone).
 * 24h: "06:00", compact "6", "6:30". 12h: "6:00 AM", "12:00 PM", compact "6 AM", "6:30 AM", "12 PM", tight "6AM".
 */
export function formatClock(t: DateTime | string, pref: ClockPref = '24h', compact: ClockCompact = false): string {
  const { h, m } = asTime(t);
  if (pref === '24h') return compact ? `${h}${m ? `:${pad(m)}` : ''}` : `${pad(h)}:${pad(m)}`;
  const h12 = h % 12 === 0 ? 12 : h % 12, ap = h < 12 ? 'AM' : 'PM';
  if (!compact) return `${h12}:${pad(m)} ${ap}`;
  return `${h12}${m ? `:${pad(m)}` : ''}${compact === 'tight' ? '' : ' '}${ap}`;
}

/** "09:00–17:30", compact "9–17:30"; 12h "9:00 AM–5:30 PM", compact "9 AM–5:30 PM", tight "9AM–5:30PM". */
export const formatClockRange = (a: DateTime | string, b: DateTime | string, pref: ClockPref = '24h', compact: ClockCompact = false) =>
  `${formatClock(a, pref, compact)}–${formatClock(b, pref, compact)}`;

export interface ShiftTimesParts {
  /** "22:00–06:00" (or compact / 12h). */
  range: string;
  /** Overnight only: the short day it ends, "Thu". */
  endDay: string | null;
  /** Overnight only: "Ends Thu 15 Oct at 06:00" for the tooltip and screen readers. */
  endsLabel: string | null;
  /** range plus the end day: "22:00–06:00 Thu". */
  text: string;
}

/** A shift's times. Start and end are DateTimes in the building's zone. Overnight shifts get the end day, not "(+1)". */
export function shiftTimeParts(start: DateTime, end: DateTime, pref: ClockPref = '24h', compact: ClockCompact = false): ShiftTimesParts {
  const range = formatClockRange(start, end, pref, compact);
  const overnight = end.toISODate() !== start.toISODate();
  const endDay = overnight ? end.setLocale('en-GB').toFormat('ccc') : null;
  const endsLabel = overnight ? `Ends ${end.setLocale('en-GB').toFormat('ccc d LLL')} at ${formatClock(end, pref)}` : null;
  return { range, endDay, endsLabel, text: endDay ? `${range} ${endDay}` : range };
}

/** "Ends Thu 15 Oct at 06:00" or null, from ISO timestamps in a zone. */
export function shiftTimesText(startsAt: string, endsAt: string, zone: string, pref: ClockPref = '24h', compact: ClockCompact = false): string {
  const a = DateTime.fromISO(startsAt, { zone: 'utc' }).setZone(zone), b = DateTime.fromISO(endsAt, { zone: 'utc' }).setZone(zone);
  return shiftTimeParts(a, b, pref, compact).text;
}

/** "8h", "7h 15m", "45m". */
export function formatDuration(minutes: number): string {
  const m = Math.max(0, Math.round(minutes)), h = Math.floor(m / 60), r = m % 60;
  return h ? (r ? `${h}h ${r}m` : `${h}h`) : `${r}m`;
}
